import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, List, Typography } from '@mui/material';
import { accountService } from '../../services/api/accountService';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import NkySnackbarAlert from '../common/NkySnackbarAlert/NkySnackbarAlert';
import AdminAssignBusinessModal from './AdminAssignBusinessModal';
import {
  UsersToolbar,
  UsersListWrap,
  UsersRowButton,
  UsersField,
  UsersSection,
  UsersSectionHeading,
  UsersSectionTitle,
  UsersSectionCount,
} from './AdminUsers.styled';

function accountDisplayName(account) {
  return `${account?.FirstName || ''} ${account?.LastName || ''}`.trim() || 'Unnamed user';
}

function compareAccountsByLastName(a, b) {
  const last = String(a?.LastName || '').localeCompare(String(b?.LastName || ''), undefined, {
    sensitivity: 'base',
  });
  if (last !== 0) return last;
  const first = String(a?.FirstName || '').localeCompare(String(b?.FirstName || ''), undefined, {
    sensitivity: 'base',
  });
  if (first !== 0) return first;
  return String(a?.Email || '').localeCompare(String(b?.Email || ''), undefined, {
    sensitivity: 'base',
  });
}

function matchesSearch(account, query, businessNameById) {
  if (!query) return true;
  const haystack = [
    account.FirstName,
    account.LastName,
    account.Email,
    account.BusinessId,
    businessNameById.get(account.BusinessId),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function AccountList({
  accounts,
  businessNameById,
  emptyLabel,
  onSelect,
  showBusinessChip,
  disabled = false,
}) {
  if (accounts.length === 0) {
    return (
      <Typography sx={{ py: 2.5, px: 0.5, color: '#5a6478' }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <UsersListWrap>
      <List disablePadding>
        {accounts.map((account) => {
          const assignedName = account.BusinessId
            ? businessNameById.get(account.BusinessId) || 'Assigned'
            : null;
          return (
            <UsersRowButton
              key={account.AccountId}
              disabled={disabled}
              onClick={disabled ? undefined : () => onSelect(account)}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  width: '100%',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: disabled ? '#94a3b8' : '#1a1a2e',
                    }}
                  >
                    {accountDisplayName(account)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: disabled ? '#94a3b8' : '#5a6478' }}
                  >
                    {account.Email}
                  </Typography>
                </Box>
                {showBusinessChip && assignedName && (
                  <Chip
                    size="small"
                    label={assignedName}
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'rgba(1, 235, 178, 0.16)',
                      color: '#0f766e',
                    }}
                  />
                )}
              </Box>
            </UsersRowButton>
          );
        })}
      </List>
    </UsersListWrap>
  );
}

function AdminUsers({
  businesses = [],
  businessesLoading = false,
  businessesError = null,
}) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const businessNameById = useMemo(() => {
    const map = new Map();
    businesses.forEach((business) => {
      const id = business?.BusinessId || business?.id;
      if (id) {
        map.set(id, business.BusinessName || id);
      }
    });
    return map;
  }, [businesses]);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await accountService.listAccountsForAdmin();
      setAccounts(
        (Array.isArray(data) ? data : []).slice().sort(compareAccountsByLastName),
      );
    } catch (e) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const query = search.trim().toLowerCase();

  const generalUserAccounts = useMemo(
    () =>
      accounts
        .filter(
          (account) =>
            account.Role === 'user' &&
            matchesSearch(account, query, businessNameById),
        )
        .sort(compareAccountsByLastName),
    [accounts, query, businessNameById],
  );

  const businessUserAccounts = useMemo(
    () =>
      accounts
        .filter(
          (account) =>
            account.Role === 'business_user' &&
            matchesSearch(account, query, businessNameById),
        )
        .sort(compareAccountsByLastName),
    [accounts, query, businessNameById],
  );

  const adminUserAccounts = useMemo(
    () =>
      accounts
        .filter(
          (account) =>
            account.Role === 'admin_user' &&
            matchesSearch(account, query, businessNameById),
        )
        .sort(compareAccountsByLastName),
    [accounts, query, businessNameById],
  );

  const showToast = (severity, message) => {
    setToast({ open: true, severity, message });
  };

  const handleAssign = async (businessId) => {
    if (!selectedAccount?.AccountId) return;
    setSaving(true);
    try {
      const updated = await accountService.assignAccountToBusiness(
        selectedAccount.AccountId,
        businessId,
      );
      setAccounts((prev) =>
        prev
          .map((account) =>
            account.AccountId === updated.AccountId
              ? { ...account, ...updated }
              : account,
          )
          .sort(compareAccountsByLastName),
      );
      setSelectedAccount(null);
      const businessName = businessNameById.get(businessId) || 'the business';
      showToast(
        'success',
        `${accountDisplayName(updated)} was added to ${businessName}.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    if (!selectedAccount?.AccountId) return;
    setSaving(true);
    try {
      const updated = await accountService.assignAccountToBusiness(
        selectedAccount.AccountId,
        '',
      );
      setAccounts((prev) =>
        prev
          .map((account) =>
            account.AccountId === updated.AccountId
              ? { ...account, ...updated }
              : account,
          )
          .sort(compareAccountsByLastName),
      );
      setSelectedAccount(null);
      showToast(
        'success',
        `${accountDisplayName(updated)} was removed from their business.`,
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, px: 2 }}>
        <NkySpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }

  const searchActive = Boolean(search.trim());

  return (
    <>
      <Typography variant="body2" sx={{ color: '#5a6478', mb: 2 }}>
        General users are consumer accounts you can add to a business. Current
        business users are already linked to a provider listing. Admin users
        are shown for reference and cannot be edited here.
      </Typography>

      <UsersToolbar>
        <UsersField
          size="small"
          label="Search users"
          placeholder="Name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240, flex: 1 }}
        />
      </UsersToolbar>

      <UsersSection>
        <UsersSectionHeading>
          <UsersSectionTitle>General Users</UsersSectionTitle>
          <UsersSectionCount>
            {generalUserAccounts.length}
            {searchActive ? ' match' : ''}
          </UsersSectionCount>
        </UsersSectionHeading>
        <AccountList
          accounts={generalUserAccounts}
          businessNameById={businessNameById}
          emptyLabel={
            searchActive
              ? `No general users match “${search.trim()}”.`
              : 'No general users to assign.'
          }
          onSelect={setSelectedAccount}
          showBusinessChip={false}
        />
      </UsersSection>

      <UsersSection>
        <UsersSectionHeading>
          <UsersSectionTitle>Current Business Users</UsersSectionTitle>
          <UsersSectionCount>
            {businessUserAccounts.length}
            {searchActive ? ' match' : ''}
          </UsersSectionCount>
        </UsersSectionHeading>
        <AccountList
          accounts={businessUserAccounts}
          businessNameById={businessNameById}
          emptyLabel={
            searchActive
              ? `No business users match “${search.trim()}”.`
              : 'No business users yet.'
          }
          onSelect={setSelectedAccount}
          showBusinessChip
        />
      </UsersSection>

      <UsersSection>
        <UsersSectionHeading>
          <UsersSectionTitle>Admin Users</UsersSectionTitle>
          <UsersSectionCount>
            {adminUserAccounts.length}
            {searchActive ? ' match' : ''}
          </UsersSectionCount>
        </UsersSectionHeading>
        <AccountList
          accounts={adminUserAccounts}
          businessNameById={businessNameById}
          emptyLabel={
            searchActive
              ? `No admin users match “${search.trim()}”.`
              : 'No admin users found.'
          }
          onSelect={setSelectedAccount}
          showBusinessChip={false}
          disabled
        />
      </UsersSection>

      <AdminAssignBusinessModal
        open={Boolean(selectedAccount)}
        account={selectedAccount}
        businesses={businesses}
        businessesLoading={businessesLoading}
        businessesError={businessesError}
        saving={saving}
        onClose={() => {
          if (!saving) setSelectedAccount(null);
        }}
        onAssign={handleAssign}
        onUnlink={handleUnlink}
      />

      <NkySnackbarAlert
        open={toast.open}
        severity={toast.severity}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}

export default AdminUsers;
